import {addDoc, collection, getFirestore, serverTimestamp} from 'firebase/firestore';
import {BUILD_COMMIT} from 'utils/general';
import {getOrCreateSessionId} from '@cip/core/utils';
import {getUserId} from 'hooks/user/useUserId';
import type {SyncMessageServiceWorkerErrorKind} from 'utils/syncMessageServiceWorker';

/**
 * Firestore collection for sync-message service-worker startup diagnostics —
 * both fatal failures and non-fatal telemetry (e.g. update timeouts).
 */
const PYODIDE_FATAL_ERROR_REPORTS_ENDPOINT = '/pyodide/fatal/errorreports';
const PYODIDE_VERSION = 2;

/**
 * Kind of sync-message service-worker startup diagnostic being reported.
 *
 * - `service-worker-disabled` — service workers unsupported/disabled (e.g. an
 *   in-app browser webview).
 * - `service-worker-tab-recovery-needed` — SW active but not controlling this
 *   tab; the user is prompted to refresh.
 * - `service-worker-registration-failed` — generic registration failure.
 * - `service-worker-update-timeout` — non-fatal telemetry: the fire-and-forget
 *   `registration.update()` did not settle in time (the app recovered; see
 *   [#3356](https://github.com/CodeinPlace/cip/issues/3356)). No user prompt.
 */
export type SyncMessageStartupErrorType =
  | 'service-worker-disabled'
  | 'service-worker-tab-recovery-needed'
  | 'service-worker-registration-failed'
  | 'service-worker-update-timeout';

/** Type and kind pairs already reported this page load, so boot and
 * `CodeProvider` reporting the same failure writes one document instead of
 * two. The kind is part of the key because several kinds share a type. */
const reportedFailures = new Set<string>();

const DEDICATED_WORKER_PROBE_TIMEOUT_MS = 1000;

/** Context for a fatal startup failure that the error alone doesn't carry. */
export interface SyncMessageStartupErrorDetails {
  /**
   * The classified cause, so reports can be grouped without parsing `message`;
   * `null` when service workers are unsupported.
   */
  errorKind?: SyncMessageServiceWorkerErrorKind | null;
  /** Failed page loads in this tab including this one, from `countFailedPageLoad`. */
  sessionFailureCount?: number;
}

/** Outcome of starting a throwaway dedicated worker from a blob URL. */
type DedicatedWorkerProbeResult = 'ok' | 'error' | 'timeout' | 'unsupported';

/**
 * Starts a trivial dedicated worker. Dedicated workers run inside the page's
 * own process, so `ok` alongside a service-worker failure points at the
 * browser failing to launch the service worker's separate process.
 */
async function probeDedicatedWorker(): Promise<DedicatedWorkerProbeResult> {
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined') {
    return 'unsupported';
  }

  let worker: Worker | null = null;
  let scriptUrl: string | null = null;
  try {
    scriptUrl = URL.createObjectURL(
      new Blob(["self.postMessage('ok');"], {type: 'text/javascript'}),
    );
    const startedWorker = new Worker(scriptUrl);
    worker = startedWorker;
    return await new Promise<DedicatedWorkerProbeResult>(resolve => {
      const timeoutId = setTimeout(
        () => resolve('timeout'),
        DEDICATED_WORKER_PROBE_TIMEOUT_MS,
      );
      startedWorker.onmessage = () => {
        clearTimeout(timeoutId);
        resolve('ok');
      };
      startedWorker.onerror = () => {
        clearTimeout(timeoutId);
        resolve('error');
      };
    });
  } catch (probeError) {
    console.debug('[SyncMessage SW] Dedicated worker probe threw', probeError);
    return 'error';
  } finally {
    worker?.terminate();
    if (scriptUrl) {
      URL.revokeObjectURL(scriptUrl);
    }
  }
}

function getNavigationType(): string | null {
  if (typeof PerformanceNavigationTiming === 'undefined') {
    return null;
  }
  const [entry] = performance.getEntriesByType('navigation');
  return entry instanceof PerformanceNavigationTiming ? entry.type : null;
}

/**
 * Writes a best-effort diagnostic document for a sync-message service-worker
 * startup event to Firestore. Never throws: a failed write is swallowed (logged
 * at warn) so diagnostics can't themselves break startup.
 *
 * @param errorType Which startup condition is being reported.
 * @param error Optional underlying error whose `message`/`stack` are captured;
 *   when omitted, `message` is `''` and `stack` is `null`.
 * @param details Optional classification context; omitted fields are stored as
 *   `null`. Fatal types also record a dedicated-worker probe.
 * @returns Resolves once the write attempt settles (success or swallowed
 *   failure), or immediately if this `errorType` and `details.errorKind` were
 *   already reported this page load.
 * @throws Never — Firestore write failures are caught and logged.
 */
export async function reportSyncMessageStartupError(
  errorType: SyncMessageStartupErrorType,
  error?: unknown,
  details: SyncMessageStartupErrorDetails = {},
): Promise<void> {
  const failureKey = `${errorType}:${details.errorKind ?? ''}`;
  if (reportedFailures.has(failureKey)) {
    return;
  }
  reportedFailures.add(failureKey);

  // Update timeouts are high-volume, non-fatal telemetry; the probe is for
  // failures the user sees.
  const dedicatedWorkerProbe =
    errorType === 'service-worker-update-timeout'
      ? null
      : await probeDedicatedWorker();

  let registrationSnapshot: Array<{
    scope: string;
    activeScriptUrl: string | null;
    waitingScriptUrl: string | null;
    installingScriptUrl: string | null;
  }> = [];

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      registrationSnapshot = registrations.map((registration) => ({
        scope: registration.scope,
        activeScriptUrl: registration.active?.scriptURL ?? null,
        waitingScriptUrl: registration.waiting?.scriptURL ?? null,
        installingScriptUrl: registration.installing?.scriptURL ?? null,
      }));
    } catch (registrationError) {
      console.warn(
        '[SyncMessage SW] Failed to snapshot service worker registrations',
        registrationError,
      );
    }
  }

  const payload = {
    errorType,
    message: error instanceof Error ? error.message : String(error ?? ''),
    stack: error instanceof Error ? error.stack ?? null : null,
    pyodideVersion: PYODIDE_VERSION,
    url: window.location.href,
    userAgent: navigator.userAgent,
    serviceWorkerSupported: 'serviceWorker' in navigator,
    controllerScriptUrl: navigator.serviceWorker?.controller?.scriptURL ?? null,
    registrationSnapshot,
    errorKind: details.errorKind ?? null,
    sessionFailureCount: details.sessionFailureCount ?? null,
    navigationType: getNavigationType(),
    dedicatedWorkerProbe,
    commit: BUILD_COMMIT,
    // Service worker startup can fire before sign-in resolves; this is
    // best-effort and may be null on first load.
    userId: getUserId(),
    sessionId: getOrCreateSessionId(),
    timestamp: new Date().toISOString(),
    createdAt: serverTimestamp(),
  };

  try {
    await addDoc(collection(getFirestore(), PYODIDE_FATAL_ERROR_REPORTS_ENDPOINT), payload);
  } catch (reportError) {
    console.warn('[SyncMessage SW] Failed to post fatal error report', reportError);
  }
}
